/**
 * API response types and request middleware.
 *
 * jQuery AJAX sets X-Requested-With: XMLHttpRequest, so Laravel
 * returns JSON errors (422, 401, 419, 500) instead of redirects.
 */

/**
 * jQuery jqXHR error object passed through the ajaxToAxios wrapper.
 */
export interface JqXHRError {
  status: number
  statusText: string
  responseJSON?: {
    message: string
    errors?: Record<string, string[]>
  }
}

/**
 * Base error shape for API errors.
 */
interface ApiErrorBase {
  status: "error"
  httpStatus: number
  message: string
}

/**
 * Validation error (HTTP 422) — field-level messages from Laravel FormRequest.
 */
export interface ValidationErrorResponse extends ApiErrorBase {
  errors: Record<string, string[]>
}

/**
 * General error (HTTP 400, 401, 403, 404, 419, 500).
 */
export interface GeneralErrorResponse extends ApiErrorBase {
  errors?: never
}

export type ApiError = ValidationErrorResponse | GeneralErrorResponse

/**
 * Type guard: check if error has field-level validation errors.
 */
export const isValidationError = (
  error: ApiError
): error is ValidationErrorResponse => {
  return typeof error.errors === "object" && error.errors !== undefined
}

/**
 * Extract first error message per field into a flat Record.
 * Useful for mapping to react-hook-form setError().
 */
export const getFieldErrors = (
  error: ValidationErrorResponse
): Record<string, string> => {
  const result: Record<string, string> = {}
  for (const [field, msgs] of Object.entries(error.errors)) {
    if (msgs.length) result[field] = msgs[0]
  }
  return result
}

/**
 * Narrow a validation error to the fields a form renders server messages for.
 * With no renderable field left it demotes to a general error carrying every
 * message, so nothing lands on a field the form can neither show nor clear.
 */
export const scopeErrorToFields = (error: ApiError, fields: string[]): ApiError => {
  if (!isValidationError(error)) return error

  const scoped = Object.entries(error.errors).filter(([field]) => fields.includes(field))
  if (scoped.length) {
    return { ...error, errors: Object.fromEntries(scoped) }
  }

  const { errors, ...general } = error
  const details = Object.values(errors).flat()
  return { ...general, message: [error.message, ...details].filter(Boolean).join("\n") }
}

/**
 * API request middleware.
 */

export type ApiResult<T = void> =
  | { ok: true; data: T; error?: undefined }
  | { ok: false; error: ApiError; data?: undefined }

const extractData = <T,>(res: unknown): T => {
  return (res !== null && typeof res === 'object' && 'data' in res)
    ? (res as { data: T }).data
    : (res as T)
}

const extractError = (err: unknown): Pick<JqXHRError, 'status' | 'statusText' | 'responseJSON'> => {
  const e = err as JqXHRError
  return { status: e.status, statusText: e.statusText, responseJSON: e.responseJSON }
}

/**
 * Server (5xx) and transport (status 0) failures get the translated
 * `general.serverError` caption — their raw message/statusText is developer
 * text. Other statuses keep the backend's message when it sent one.
 */
const humanizeErrorMessage = (
  status: number,
  statusText: string,
  responseJSON: JqXHRError["responseJSON"],
): string => {
  const caption = window.labels?.general?.serverError
  if (status >= 500 || status === 0) {
    return caption || responseJSON?.message || statusText || "Unknown error"
  }
  return responseJSON?.message || caption || statusText || "Unknown error"
}

export async function apiRequest<T = void>(
  promise: Promise<T>
): Promise<ApiResult<T>> {
  try {
    const res = await promise
    return { ok: true, data: extractData<T>(res) }
  } catch (err) {
    const { status, statusText, responseJSON } = extractError(err)
    const message = humanizeErrorMessage(status, statusText, responseJSON)
    const base: ApiErrorBase = { status: "error", httpStatus: status, message }

    if (status === 422 && responseJSON?.errors) {
      return { ok: false, error: { ...base, errors: responseJSON.errors } }
    }

    return { ok: false, error: base as GeneralErrorResponse }
  }
}
